import React from 'react';
import './HorizontalTimeBar.css';
import type { MatchSlot } from '@iziping/common';
import { useTranslation } from 'react-i18next';

export interface HorizontalTimeBarProps {
    label: string;
    slots: MatchSlot[];
    startHour?: number;
    endHour?: number;
    currentTime?: Date;
    isEven?: boolean;
    referenceDate?: Date;
    onSlotClick?: (slot: MatchSlot, rect: DOMRect) => void;
}

const parseTimeToMinutes = (input: Date | string, referenceDate?: Date): number => {
    const date = typeof input === 'string' ? new Date(input) : input;
    const crossesMidnight = referenceDate !== undefined && date.getDate() !== referenceDate.getDate();
    return (date.getHours() + (crossesMidnight ? 24 : 0)) * 60 + date.getMinutes();
};

const getSlotStatus = (slot: MatchSlot, now: Date): 'finished' | 'active' | 'upcoming' => {
    const begin = new Date(slot.begin);
    const end = new Date(slot.end);
    if (end < now) return 'finished';
    if (begin <= now && now <= end) return 'active';
    return 'upcoming';
};

const HorizontalTimeBar: React.FC<HorizontalTimeBarProps> = ({
    label,
    slots,
    startHour = 8,
    endHour = 24,
    currentTime,
    isEven = false,
    referenceDate,
    onSlotClick,
}) => {
    const { t } = useTranslation();
    const totalMinutes = (endHour - startHour) * 60;
    const now = currentTime ?? new Date();

    return (
        <div className="timeline-row">
            <div className="timeline-label">{`${t('TABLE')} ${label}`}</div>

            <div className={`timeline-track${isEven ? ' timeline-track--even' : ''}`}>
                {slots.map((slot, idx) => {
                    const startMin = parseTimeToMinutes(slot.begin, referenceDate) - startHour * 60;
                    const endMin = parseTimeToMinutes(slot.end, referenceDate) - startHour * 60;
                    const left = (startMin / totalMinutes) * 100;
                    const width = ((endMin - startMin) / totalMinutes) * 100;
                    const status = getSlotStatus(slot, now);

                    return (
                        <div
                            key={idx}
                            className={`timeline-slot timeline-slot--${status}${onSlotClick ? ' timeline-slot--clickable' : ''}`}
                            style={{ left: `${left}%`, width: `${width}%` }}
                            title={slot.label}
                            onClick={
                                onSlotClick
                                    ? (e) => onSlotClick(slot, e.currentTarget.getBoundingClientRect())
                                    : undefined
                            }
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default HorizontalTimeBar;
